// Chatrooms.jsx
/* FIXME: [Chatrooms] issue - in the chat list. sometimes can't fetch next page when the scroll down. (Safari / PWA)
 */
'use strict';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import { useInView } from 'react-intersection-observer';
import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';

import PullToRefresh from '../container/PullToRefresh.js';
import ChatroomItem from '../container/ChatroomItem.js';
import AutoChatChatroomItem from '../container/AutoChatChatroomItem.js';
import UserCategory from '../container/UserCategory.js';
import GridDiscoverUsers from '../container/GridDiscoverUsers.js';

import Spinning from '../style/Spinning.js';
import {
  breakpoint,
  color,
  textColor,
  footerNavigationHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';

import LoadingImage from '../../img/loading.png';
import ChatroomAddIcon from '../../img/ic_add_msg.svg';
import ShopIconSource from '../../img/ic_diamond_shop.svg';
import {
  chatroomListType,
  DESKTOP_TOP_MARGIN_OFFSET,
  DESKTOP_CHATROOMS_WIDTH,
} from '../resource/chatroomConstants.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { notificationList } from '../resource/notificationConstants.js';
import { CHAT_ID as AUTO_CHAT_CHAT_ID } from '../resource/autoChatConstants.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';

import { headerHeight } from '../style/variables.js';

const OBSERVER_OPTIONS = {
  triggerOnce: true,
};

const VirtualizeSwipeableViews = virtualize(SwipeableViews);
const CHATROOM_TABS = [
  chatroomListType.ALL,
  chatroomListType.UNREAD,
  chatroomListType.PIN,
];

function Chatrooms({
  itemIds: propsItemIds = EMPTY_ARRAY,
  configurations = EMPTY_ARRAY,
  nextPage,
  isNextPageFetching,
  isAuthed = false,
  isDiamondShopOpen = false,
  fetchChatrooms = NULL_FUNCTION,
  fetchPinnedChatrooms = NULL_FUNCTION,
  isFirstPageFetching = false,
  isFirstPageFetched = false,
  hasAutoChat = false,
  hasUnreadAutoChat = false,
  isOnDesktop = false,
  hasChatrooms = false,
  scrollTop = 0,
  deviceHeight = 0,
  previousRoute = '',
  currentRoute = '',
  activeId = '',
  setScrollerPositionData = NULL_FUNCTION,
  listType = chatroomListType.ALL,
  setChatroomTab = NULL_FUNCTION,
  fetchUnreadChatrooms = NULL_FUNCTION,
  fetchMeNotifications: fetchMeNotificationsProp = NULL_FUNCTION,
  addvalue = NULL_FUNCTION,
  apiCacheTimestamp = 0,
  searchSwaggerOnlineFeed = null,
}) {
  const { t } = useTranslation();
  const [itemIds, setItemIds] = useState(EMPTY_ARRAY);
  const nextTickFetchDataRef = useRef(null);
  const nextTickSetStateRef = useRef(null);

  const fetchMeNotifications = useCallback(
    ({ page, listType: lt }) => {
      fetchMeNotificationsProp({ page, listType: lt });
    },
    [fetchMeNotificationsProp]
  );

  const fetchChatroomsForType = useCallback(
    ({ page = 1 }) => {
      if (listType === chatroomListType.ALL) {
        fetchChatrooms({ page });
      } else if (listType === chatroomListType.UNREAD) {
        fetchUnreadChatrooms({ page });
      } else {
        fetchPinnedChatrooms({ page, listType: chatroomListType.PIN });
      }
    },
    [listType, fetchChatrooms, fetchUnreadChatrooms, fetchPinnedChatrooms]
  );

  const restoreScrollTop = useCallback(() => {
    const isPreviousRouteOnChat = /\/chat\/[a-z\d]+/i.test(previousRoute);
    const isOnRoute = currentRoute && currentRoute.includes('/chat');
    if (isOnRoute && isPreviousRouteOnChat && propsItemIds?.length) {
      setTimeout(() => {
        window.scrollTo(0, scrollTop);
      }, 0);
    }
  }, [previousRoute, currentRoute, propsItemIds, scrollTop]);

  const fetchMeNotificationsRef = useRef(fetchMeNotifications);
  fetchMeNotificationsRef.current = fetchMeNotifications;
  const fetchChatroomsForTypeRef = useRef(fetchChatroomsForType);
  fetchChatroomsForTypeRef.current = fetchChatroomsForType;
  const restoreScrollTopRef = useRef(restoreScrollTop);
  restoreScrollTopRef.current = restoreScrollTop;
  const propsItemIdsRef = useRef(propsItemIds);
  propsItemIdsRef.current = propsItemIds;

  useEffect(() => {
    nextTickFetchDataRef.current = setTimeout(() => {
      fetchMeNotificationsRef.current({
        page: 1,
        listType: notificationList.AUTO_CHAT,
      });
      fetchChatroomsForTypeRef.current({ page: 1 });
      setItemIds(propsItemIdsRef.current);
      restoreScrollTopRef.current();
    }, 0);
    return () => {
      clearTimeout(nextTickFetchDataRef.current);
      clearTimeout(nextTickSetStateRef.current);
    };
  }, []);

  useEffect(() => {
    clearTimeout(nextTickSetStateRef.current);
    nextTickSetStateRef.current = setTimeout(() => setItemIds(propsItemIds), 0);
  }, [propsItemIds]);

  const prevIsAuthedRef = useRef(isAuthed);
  useEffect(() => {
    if (isAuthed === prevIsAuthedRef.current) return;
    prevIsAuthedRef.current = isAuthed;
    if (!isAuthed) return;
    clearTimeout(nextTickFetchDataRef.current);
    nextTickFetchDataRef.current = setTimeout(() => {
      fetchMeNotificationsRef.current({
        page: 1,
        listType: notificationList.AUTO_CHAT,
      });
      fetchChatroomsForTypeRef.current({ page: 1 });
    }, 0);
  }, [isAuthed]);

  const prevListTypeRef = useRef(listType);
  const prevApiCacheTimestampRef = useRef(apiCacheTimestamp);
  useEffect(() => {
    if (
      listType === prevListTypeRef.current &&
      apiCacheTimestamp === prevApiCacheTimestampRef.current
    )
      return;
    prevListTypeRef.current = listType;
    prevApiCacheTimestampRef.current = apiCacheTimestamp;
    clearTimeout(nextTickFetchDataRef.current);
    nextTickFetchDataRef.current = setTimeout(() => {
      fetchChatroomsForTypeRef.current({ page: 1 });
    }, 0);
  }, [listType, apiCacheTimestamp]);

  const prevConfigurationsRef = useRef(configurations);
  useEffect(() => {
    if (configurations === prevConfigurationsRef.current) return;
    prevConfigurationsRef.current = configurations;
    fetchMeNotificationsRef.current({
      page: 1,
      listType: notificationList.AUTO_CHAT,
    });
  }, [configurations]);

  const handleRefresh = useCallback(async () => {
    fetchMeNotifications({ page: 1, listType: notificationList.AUTO_CHAT });
    fetchChatroomsForType({ page: 1 });
  }, [fetchMeNotifications, fetchChatroomsForType]);

  const scrollToTop = useCallback(
    () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    []
  );

  const renderChatroomItem = useCallback(
    id => <ObservableChatroomItem key={id} id={id} activeId={activeId} />,
    [activeId]
  );

  const shouldShowAutoChat =
    chatroomListType.ALL === listType
      ? hasAutoChat
      : chatroomListType.UNREAD === listType
        ? hasUnreadAutoChat
        : false;

  const shouldShowActiveNewChatroom =
    isOnDesktop &&
    activeId &&
    !itemIds.includes(activeId) &&
    listType === chatroomListType.ALL;

  const isLoading =
    isAuthed && propsItemIds.length === 0 && !nextPage && isFirstPageFetching;
  const isEmpty =
    !isAuthed ||
    (propsItemIds.length === 0 && isFirstPageFetched && !shouldShowAutoChat);
  const shouldShowUserList =
    (isOnDesktop && hasChatrooms) || (!isOnDesktop && !isEmpty);

  const isLengthDifferent = itemIds.length !== propsItemIds.length;

  return (
    <StyledChatrooms
      isIphoneXSeriesPWA={getIsIphoneXSeriesPWA()}
      deviceHeight={deviceHeight}
    >
      <Header>
        <HeaderLeft>
          <HeaderTitle onClick={scrollToTop}>
            {t('chatroom_list_title', { ns: TranslationNamespace.CHAT })}
          </HeaderTitle>
        </HeaderLeft>
        <HeaderRight>
          <NewChatroomIcon
            as={Link}
            to="/chat/start"
            title={t('swagr_create_chatroom', {
              ns: TranslationNamespace.CHAT,
            })}
            data-element_id={ButtonId.Chatroom.ButtonNewChatroom}
          >
            <img
              src={ChatroomAddIcon}
              alt={t('swagr_create_chatroom', {
                ns: TranslationNamespace.CHAT,
              })}
            />
          </NewChatroomIcon>
          <ShopButton
            isMatch={isDiamondShopOpen}
            title={t('buy_points', { ns: TranslationNamespace.GENERAL })}
            data-element_id={ButtonId.All.ButtonDiamondShop}
            onClick={addvalue}
          >
            <img
              src={ShopIconSource}
              alt={t('buy_points', { ns: TranslationNamespace.CHAT })}
            />
          </ShopButton>
        </HeaderRight>
        <ChatTabs>
          <Tabs
            value={listType}
            onChange={(_, lt) => {
              setChatroomTab({ listType: lt });
            }}
            style={{ width: '100%' }}
          >
            <Tab
              as="button"
              value={chatroomListType.ALL}
              label={t('chatlist_all_messages', {
                ns: TranslationNamespace.CHAT,
              })}
            />
            <Tab
              as="button"
              value={chatroomListType.UNREAD}
              label={t('chatlist_all_unread', {
                ns: TranslationNamespace.CHAT,
              })}
            />
            <Tab
              as="button"
              value={chatroomListType.PIN}
              label={t('pin', { ns: TranslationNamespace.CHAT })}
            />
          </Tabs>
        </ChatTabs>
      </Header>
      {isLoading ? (
        <Loader>
          <img src={LoadingImage} />
        </Loader>
      ) : (
        <>
          {shouldShowUserList && (
            <UserCategory
              category={searchSwaggerOnlineFeed}
              shouldDisplayHeader={false}
              avatarSize={64}
              style={{
                backgroundColor: `${color.black}`,
                paddingTop: '14px',
                height: '144px',
              }}
              withChatLink
              usernameMarginTop={6}
              marginRight={0}
            />
          )}
          <PullToRefresh useWindow onRefresh={handleRefresh}>
            <Body>
              <VirtualizeSwipeableViews
                index={CHATROOM_TABS.indexOf(listType)}
                onChangeIndex={sliderIndex => {
                  const newListType = CHATROOM_TABS[sliderIndex];
                  setChatroomTab({ listType: newListType });
                }}
                slideCount={CHATROOM_TABS.length}
                slideRenderer={({ key }) =>
                  isEmpty ? (
                    <div key={key}>
                      <Placeholder>
                        <PlaceholderText>
                          {listType === chatroomListType.ALL &&
                            t('chat_empty_message', {
                              ns: TranslationNamespace.CHAT,
                            })}
                          {listType === chatroomListType.UNREAD &&
                            t('chatlist_no_new_messages', {
                              ns: TranslationNamespace.CHAT,
                            })}
                          {listType === chatroomListType.PIN &&
                            t('no_pin_chatroom', {
                              ns: TranslationNamespace.CHAT,
                            })}
                        </PlaceholderText>
                      </Placeholder>
                      {!isOnDesktop && (
                        <GridDiscoverUsers
                          category={searchSwaggerOnlineFeed}
                          withChatLink
                          size={104}
                          marginTop={12}
                        />
                      )}
                    </div>
                  ) : (
                    <InfiniteScroller
                      key={key}
                      pageStart={1}
                      hasMore={nextPage != null}
                      loadMore={() => fetchChatroomsForType({ page: nextPage })}
                      isFetching={isNextPageFetching || isLengthDifferent}
                      threshold={100}
                      recordPosition={scrollerPositionData =>
                        setScrollerPositionData({
                          scrollerPositionData,
                          page: 'chatrooms',
                        })
                      }
                    >
                      <ChatroomsList>
                        <ScrollItem
                          loader={
                            <Loader>
                              <img src={LoadingImage} />
                            </Loader>
                          }
                        >
                          {shouldShowAutoChat &&
                            renderChatroomItem(AUTO_CHAT_CHAT_ID)}
                          {shouldShowActiveNewChatroom &&
                            renderChatroomItem(activeId)}
                          {itemIds.map(renderChatroomItem)}
                        </ScrollItem>
                      </ChatroomsList>
                    </InfiniteScroller>
                  )
                }
              />
            </Body>
          </PullToRefresh>
        </>
      )}
    </StyledChatrooms>
  );
}

Chatrooms.propTypes = {
  itemIds: PropTypes.arrayOf(PropTypes.string),
  configurations: PropTypes.array,
  nextPage: PropTypes.number,
  isNextPageFetching: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isDiamondShopOpen: PropTypes.bool,
  fetchChatrooms: PropTypes.func,
  fetchPinnedChatrooms: PropTypes.func,
  isFirstPageFetching: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  hasAutoChat: PropTypes.bool,
  hasUnreadAutoChat: PropTypes.bool,
  isOnDesktop: PropTypes.bool,
  hasChatrooms: PropTypes.bool,
  scrollTop: PropTypes.number,
  deviceHeight: PropTypes.number,
  previousRoute: PropTypes.string,
  currentRoute: PropTypes.string,
  setScrollerPositionData: PropTypes.func,
  listType: PropTypes.string,
  activeId: PropTypes.string,
  setChatroomTab: PropTypes.func,
  fetchUnreadChatrooms: PropTypes.func,
  addvalue: PropTypes.func,
  fetchMeNotifications: PropTypes.func,
  apiCacheTimestamp: PropTypes.number,
  searchSwaggerOnlineFeed: PropTypes.string,
};

const ObservableChatroomItem = ({ id, activeId }) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);
  return (
    <IntersectionObserverWrapper ref={ref}>
      <ChatroomItemWrapper>
        {id === AUTO_CHAT_CHAT_ID ? (
          <AutoChatChatroomItem
            id={AUTO_CHAT_CHAT_ID}
            isIntersecting={inView}
          />
        ) : (
          <ChatroomItem
            id={id}
            isIntersecting={inView}
            isActive={activeId === id}
          />
        )}
      </ChatroomItemWrapper>
    </IntersectionObserverWrapper>
  );
};

ObservableChatroomItem.propTypes = {
  id: PropTypes.string,
  activeId: PropTypes.string,
};

const StyledChatrooms = styled.div`
  --space-to-styled-chatroom-top: 108px;
  height: ${({ deviceHeight }) =>
    `calc(${deviceHeight}px - ${headerHeight}px - var(--space-to-pinned-notification))`};
  overflow-y: auto;
  padding-top: var(--space-to-styled-chatroom-top);
  padding-bottom: 0;
  @media (max-width: ${breakpoint.mobile}px) {
    height: auto;
    overflow-y: hidden;
    padding-top: calc(
      var(--space-to-styled-chatroom-top) + var(--space-to-pinned-notification)
    );
    padding-bottom: ${({ isIphoneXSeriesPWA }) =>
      (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0) +
      footerNavigationHeight}px;
  }
  ::-webkit-scrollbar {
    display: none;
  }
`;

const Header = styled.div`
  flex: none;
  position: fixed;
  top: ${DESKTOP_TOP_MARGIN_OFFSET}px;
  width: ${DESKTOP_CHATROOMS_WIDTH}px;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${headerHeight}px;
  z-index: 3;
  background-color: ${color.black};
  @media (max-width: ${breakpoint.tablet}px) {
    top: calc(
      ${DESKTOP_TOP_MARGIN_OFFSET}px + var(--space-to-pinned-notification)
    );
  }
  @media (max-width: ${breakpoint.mobile}px) {
    top: var(--space-to-pinned-notification);
    width: 100%;
  }
`;

const HeaderSection = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
`;
const HeaderLeft = styled(HeaderSection)`
  top: 50%;
  left: 16px;
`;

const HeaderRight = styled(HeaderSection)`
  top: 50%;
  right: 16px;
  font-size: 12px;
`;

const HeaderTitle = styled.button`
  ${ResetButtonStyle};
  color: ${textColor.white};
  font-size: 22px;
  font-weight: 600;
`;

const Body = styled.div`
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
`;

const Icon = styled.button`
  ${ResetButtonStyle};
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: 0.3s;
  background-color: ${({ isMatch }) =>
    isMatch ? 'rgba(0, 126, 114, 0.2)' : 'transparent'};
  opacity: 1;
  & + & {
    margin-left: 8px;
  }
  > img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
  @media (hover: hover) {
    :hover {
      background-color: ${color.neutral['55_alpha_20']};
      opacity: 1;
    }
  }
  ${media.mobile`
    & + & {
      margin-left: 4px;
    }
  `};
`;

const ShopButton = styled(Icon)``;
const NewChatroomIcon = styled(Icon)``;

const Placeholder = styled.div`
  flex: auto;
  margin-top: 20px;
  margin-bottom: 28px;
`;

const PlaceholderText = styled.div`
  color: #646464;
  font-size: 14px;
  text-align: center;
`;

const ChatroomsList = styled.div`
  flex: auto;
`;

const Loader = styled.div`
  text-align: center;
  padding: 8px 0;
  margin-top: 32px;
  width: 100%;
  & > img {
    width: 32px;
    animation: 3s infinite ${Spinning};
  }
`;

const IntersectionObserverWrapper = styled.div``;

const ChatroomItemWrapper = styled.div``;

const ChatTabs = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  height: 39px;
  top: calc(
    ${DESKTOP_TOP_MARGIN_OFFSET}px + ${headerHeight}px +
      var(--space-to-pinned-notification)
  );
  width: ${DESKTOP_CHATROOMS_WIDTH}px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-bottom: 1px solid ${color.neutral['30_alpha_15']};
  background-color: ${color.black};

  @media (max-width: ${breakpoint.mobile}px) {
    top: calc(var(--space-to-pinned-notification) + ${headerHeight}px);
    width: 100%;
  }
`;

export { Chatrooms };
export default React.memo(Chatrooms, (prev, next) => {
  const { scrollTop: _p, ...prevRest } = prev;
  const { scrollTop: _n, ...nextRest } = next;
  return shallowEqual(prevRest, nextRest);
});
